import { clsx } from 'clsx';
import { type FC, useCallback, useEffect, useRef } from 'react';

import { useMapContext } from '@/microfrontends/map/shared/context/MapContext';
import { useMapFeaturesContext } from '@/microfrontends/map/shared/context/MapLayersCenterContext';
import { PinService } from '@/microfrontends/map/shared/services/PinService';
import { type HotspotProperties } from '@/microfrontends/map/shared/types/Map';
import { PIN_EXTENDED_TYPES } from '@/microfrontends/map/shared/utils/mapModules/PinsLayer/types';

import { useActivePinIdSelector } from '@/packages/redux-state/store/slices/mapControls';

import styles from './MapHoverPinUpdater.css';

export const MapHoverPinUpdater: FC = () => {
  const { ymaps, map } = useMapContext();
  const { hotspotsLayer } = useMapFeaturesContext();
  const activePinId = useActivePinIdSelector();

  const prepareIconLayout = useCallback(
    (pinService: PinService) => {
      const extendedTypes = pinService.provideExtendedTypes();
      const withoutCount = !extendedTypes.has(PIN_EXTENDED_TYPES.withCount);
      const count = pinService.provideCount();

      const className = clsx(
        styles['container'],
        styles['container--blue'],
        withoutCount && styles['container--small'],
      );

      return ymaps.templateLayoutFactory.createClass(`<div class="${className}">${count}</div>`);
    },
    [ymaps.templateLayoutFactory],
  );

  const placemarkObject = useRef<YMaps.GeoObject | null>(null);

  const handleHotspotMouseEnter = useCallback(
    (event: YMaps.IEvent) => {
      const activeObject = event.get<YMaps.IHotspotLayerObject<HotspotProperties>>('activeObject');
      const { clusterData: cluster } = activeObject.getProperties();
      const isHoverOnActivePin = activePinId === cluster.id;

      const pinService = new PinService({ cluster });

      if (isHoverOnActivePin) {
        return;
      }

      if (!pinService.isPinInteractive) {
        return;
      }

      const extendedTypes = pinService.provideExtendedTypes();
      const withBalloon = extendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

      if (withBalloon) {
        return;
      }

      placemarkObject.current = new ymaps.GeoObject(cluster, { iconLayout: prepareIconLayout(pinService) });

      map.geoObjects.add(placemarkObject.current);
    },
    [map.geoObjects, ymaps.GeoObject, activePinId, prepareIconLayout],
  );

  const handleHotspotMouseLeave = useCallback(() => {
    if (placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }
  }, [map.geoObjects]);

  useEffect(() => {
    hotspotsLayer.layer.events.add('mouseenter', handleHotspotMouseEnter);
    hotspotsLayer.layer.events.add('mouseleave', handleHotspotMouseLeave);

    return () => {
      hotspotsLayer.layer.events.remove('mouseenter', handleHotspotMouseEnter);
      hotspotsLayer.layer.events.remove('mouseleave', handleHotspotMouseLeave);
    };
  }, [hotspotsLayer.layer, handleHotspotMouseEnter, handleHotspotMouseLeave]);

  useEffect(() => {
    if (activePinId && placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }
  }, [map.geoObjects, activePinId]);

  return null;
};
